import { useState } from 'react';
import { Icon, Spinner } from './ui/Icon.js';
import type { PipelineStatus } from '../types/index.js';

interface PipelineSummaryBarProps {
  status: PipelineStatus;
  onRecomputeScores: () => Promise<void>;
  onRedetectGaps: () => Promise<void>;
  onRecomputeDomainScores: () => Promise<void>;
}

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return 'never';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

function getMostRecentRun(status: PipelineStatus): string | null {
  const dates = [
    status.queries.last_executed_at,
    status.analysis.last_analyzed_at,
    status.visibility.last_computed_at,
    status.gaps.last_detected_at,
    status.domain_scores.last_computed_at,
  ].filter((d): d is string => d != null);

  if (dates.length === 0) return null;
  return dates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0]!;
}

type StepStatus = 'complete' | 'stale' | 'never';

function getStepStatus(lastRun: string | null, isStale?: boolean): StepStatus {
  if (!lastRun) return 'never';
  if (isStale) return 'stale';
  return 'complete';
}

function StepIndicator({ status }: { status: StepStatus }) {
  if (status === 'complete') {
    return <Icon name="bx-check-circle" className="text-green-500" />;
  }
  if (status === 'stale') {
    return <Icon name="bx-error" className="text-amber-500" />;
  }
  return <Icon name="bx-circle" className="text-slate-300" />;
}

/**
 * Spec 248 — one slot of the pipeline definition row. The count is the value;
 * a step that has never run says so in words, and a stale one carries a
 * status chip (Kridt: chips are status only). No glyph carries the meaning.
 */
function PipelineStep({
  label,
  count,
  status,
  extra,
  testId,
}: {
  label: string;
  count: number;
  status: StepStatus;
  extra?: string;
  testId: string;
}) {
  return (
    <div className="hub-deflist__item" data-status={status} data-testid={testId}>
      <dt>{label}</dt>
      <dd>
        {status === 'never' ? (
          <span className="text-[var(--hub-color-ink-muted)]">Not run yet</span>
        ) : (
          <>
            {count}
            {extra && <span className="hub-deflist__extra">, {extra}</span>}
            {status === 'stale' && <span className="hub-chip hub-chip--warn ml-2">Stale</span>}
          </>
        )}
      </dd>
    </div>
  );
}

interface ActionButtonProps {
  onClick: () => Promise<void>;
  label: string;
}

function ActionButton({ onClick, label }: ActionButtonProps) {
  const [running, setRunning] = useState(false);

  const handleClick = async () => {
    if (running) return;
    setRunning(true);
    try {
      await onClick();
    } finally {
      setRunning(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={running}
      className="hub-btn hub-btn--quiet"
      title={label}
      aria-label={label}
    >
      {running ? (
        <Spinner  />
      ) : (
        <Icon name="bx-refresh" />
      )}
    </button>
  );
}

export function PipelineSummaryBar({
  status,
  onRecomputeScores,
  onRedetectGaps,
  onRecomputeDomainScores,
}: PipelineSummaryBarProps) {
  const [expanded, setExpanded] = useState(false);

  const lastUpdated = getMostRecentRun(status);
  const queriesStatus = getStepStatus(status.queries.last_executed_at);
  const analysisStatus = getStepStatus(status.analysis.last_analyzed_at);
  const scoresStatus = getStepStatus(status.visibility.last_computed_at, status.visibility.is_stale);
  const gapsStatus = getStepStatus(status.gaps.last_detected_at, status.gaps.is_stale);
  const sourcesStatus = getStepStatus(status.domain_scores.last_computed_at, status.domain_scores.is_stale);

  const hasStale = scoresStatus === 'stale' || gapsStatus === 'stale' || sourcesStatus === 'stale';

  return (
    <section
      // Spec 248: no amber wash on the whole strip when one step is stale —
      // the tone belongs on the step that is stale, as a chip, not on the panel.
      className="mt-3 hub-plate--flat px-4 py-2"
      data-testid="pipeline-status-bar"
      data-stale={hasStale || undefined}
    >
      {/* Spec 248 (review P1-6): a definition row — each count in its own slot,
          divided by hairlines — replacing "○ 0 queries · ○ 0 analyzed · …".
          That strip repeated the stat row in a smaller voice, in middle dots and
          hollow circles. The circles were not decoration: each step is complete,
          stale or never run. So the status survives, but as WORDS — "not run
          yet", and a "Stale" chip — rather than as a glyph a reader has to
          decode (and that fails in greyscale). */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <dl className="hub-deflist" data-testid="pipeline-steps">
          <PipelineStep label="Queries" count={status.queries.total} status={queriesStatus} testId="pipeline-step-queries" />
          <PipelineStep
            label="Analyzed"
            count={status.analysis.total_analyzed}
            status={analysisStatus}
            extra={status.analysis.pending > 0 ? `${status.analysis.pending} pending` : undefined}
            testId="pipeline-step-analyzed"
          />
          <PipelineStep label="Scores" count={status.visibility.score_count} status={scoresStatus} testId="pipeline-step-scores" />
          <PipelineStep label="Gaps" count={status.gaps.gap_count} status={gapsStatus} testId="pipeline-step-gaps" />
          <PipelineStep label="Sources" count={status.domain_scores.score_count} status={sourcesStatus} testId="pipeline-step-sources" />
        </dl>

        {lastUpdated && (
          <span className="text-xs text-[var(--hub-color-ink-muted)]">Updated {formatRelativeTime(lastUpdated)}</span>
        )}

        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          className="hub-btn hub-btn--quiet ml-auto"
          data-testid="pipeline-details-toggle"
        >
          {expanded ? 'Hide details' : 'Details'}
          {expanded ? <Icon name="bx-chevron-up" /> : <Icon name="bx-chevron-down" />}
        </button>
      </div>

      {/* Expanded detail row */}
      {expanded && (
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-200/60 pt-2 text-xs text-slate-600">
          <div className="flex items-center gap-1.5">
            <StepIndicator status={queriesStatus} />
            <span className="font-medium">Queries:</span>
            <span>{status.queries.total} completed</span>
            <span className="text-slate-400">{formatRelativeTime(status.queries.last_executed_at)}</span>
          </div>

          <span className="hidden text-slate-200 sm:inline">|</span>

          <div className="flex items-center gap-1.5">
            <StepIndicator status={analysisStatus} />
            <span className="font-medium">Analysis:</span>
            <span>{status.analysis.total_analyzed} analyzed</span>
            {status.analysis.pending > 0 && (
              <span className="text-amber-600">({status.analysis.pending} pending)</span>
            )}
            <span className="text-slate-400">{formatRelativeTime(status.analysis.last_analyzed_at)}</span>
          </div>

          <span className="hidden text-slate-200 sm:inline">|</span>

          <div className="flex items-center gap-1.5">
            <StepIndicator status={scoresStatus} />
            <span className="font-medium">Scores:</span>
            <span>{status.visibility.score_count}</span>
            <span className="text-slate-400">{formatRelativeTime(status.visibility.last_computed_at)}</span>
            <ActionButton onClick={onRecomputeScores} label="Recompute scores" />
          </div>

          <span className="hidden text-slate-200 sm:inline">|</span>

          <div className="flex items-center gap-1.5">
            <StepIndicator status={gapsStatus} />
            <span className="font-medium">Gaps:</span>
            <span>{status.gaps.gap_count}</span>
            <span className="text-slate-400">{formatRelativeTime(status.gaps.last_detected_at)}</span>
            <ActionButton onClick={onRedetectGaps} label="Re-detect gaps" />
          </div>

          <span className="hidden text-slate-200 sm:inline">|</span>

          <div className="flex items-center gap-1.5">
            <StepIndicator status={sourcesStatus} />
            <span className="font-medium">Sources:</span>
            <span>{status.domain_scores.score_count}</span>
            <span className="text-slate-400">{formatRelativeTime(status.domain_scores.last_computed_at)}</span>
            <ActionButton onClick={onRecomputeDomainScores} label="Recompute sources" />
          </div>
        </div>
      )}
    </section>
  );
}
